import 'bootstrap-icons/font/bootstrap-icons.css'; // Add this import for Bootstrap Icons
import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';


function BusinessDuserReAudit() {
  const [checklists, setChecklists] = useState({
    pending: [],
    inProgress: [],
    completed: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [checklistContent, setChecklistContent] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedSubsection, setSelectedSubsection] = useState(null);
  const [subsectionQuestions, setSubsectionQuestions] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [expandedSections, setExpandedSections] = useState({});

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem('dUser'));
  const CompanyCode = userData?.CompanyCode;
  const department = userData?.department;

  // Get tab from URL if present
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tabParam = queryParams.get('tab');

  useEffect(() => {
    // Set active tab based on URL parameter if present
    if (tabParam) {
      if (tabParam === 'all') {
        setActiveTab('pending'); // Default to pending when "all" is selected
      } else if (['pending', 'inProgress', 'completed'].includes(tabParam)) {
        setActiveTab(tabParam);
      }
    }
  }, [tabParam]);

  useEffect(() => {
    if (CompanyCode && department) {
      fetchDepartmentReauditChecklists();
    }
  }, [CompanyCode, department]);

  const fetchDepartmentReauditChecklists = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `http://localhost:8081/get-business-department-reaudit-checklists/${CompanyCode}/${department}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const checklistsData = data.checklists;
        
        // Categorize checklists by status
        const categorized = {
          pending: checklistsData.filter(cl => cl.status === 'Pending'),
          inProgress: checklistsData.filter(cl => cl.status === 'In progress'),
          completed: checklistsData.filter(cl => cl.status === 'Completed')
        };

        setChecklists(categorized);
      }
    } catch (error) {
      console.error('Error fetching business re-audit checklists:', error);
      alert('Failed to fetch business re-audit checklists');
    } finally {
      setLoading(false);
    }
  };

  const fetchChecklistContent = async (reauditId) => {
    try {
      const response = await fetch(
        `http://localhost:8081/get-business-department-reaudit-content/${reauditId}/${CompanyCode}/${department}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setChecklistContent(data.content);
        
        // Fetch questions for this department
        const questionsResponse = await fetch(
          `http://localhost:8081/get-business-department-reaudit-questions/${reauditId}/${CompanyCode}/${department}`
        );

        if (!questionsResponse.ok) {
          throw new Error(`HTTP error! Status: ${questionsResponse.status}`);
        }

        const questionsData = await questionsResponse.json();

        if (questionsData.success) {
          setQuestions(questionsData.questions);
        }
      }
    } catch (error) {
      console.error('Error fetching business re-audit checklist content:', error);
      alert('Failed to fetch business re-audit checklist content');
    }
  };

  const fetchSubsectionQuestions = async (reauditId, subsectionPath) => {
    try {
      const response = await fetch(
        `http://localhost:8081/get-business-department-reaudit-subsection-questions/${reauditId}/${CompanyCode}/${department}/${subsectionPath}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setSubsectionQuestions(data.questions);
        setSelectedSubsection(data.subsection);
      }
    } catch (error) {
      console.error('Error fetching business re-audit subsection questions:', error);
      alert('Failed to fetch business re-audit subsection questions');
    }
  };

  const handleChecklistClick = (checklist) => {
    setSelectedChecklist(checklist);
    setChecklistContent(null);
    setQuestions([]);
    setSelectedSubsection(null);
    setSubsectionQuestions([]);
    // Use the re-audit checklist id directly
    fetchChecklistContent(checklist.id);
  };

  const handleSubsectionClick = (subsection, path = '') => {
    const subsectionPath = path ? `${path}|${subsection.name}` : subsection.name;
    // Use the re-audit checklist id directly
    fetchSubsectionQuestions(selectedChecklist.id, subsectionPath);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'badge bg-warning text-dark';
      case 'in progress':
        return 'badge bg-info text-dark';
      case 'completed':
        return 'badge bg-success';
      default:
        return 'badge bg-secondary';
    }
  };

  const renderQuestionDetails = (question) => {
    let answerOptions = [];
    let optionPoints = [];

    try {
      answerOptions = typeof question.answerOptions === 'string' 
        ? JSON.parse(question.answerOptions) 
        : question.answerOptions || [];
      
      optionPoints = typeof question.optionPoints === 'string'
        ? JSON.parse(question.optionPoints)
        : question.optionPoints || [];
    } catch (error) {
      console.error('Error parsing question options:', error);
    }

    // Check if this question has a response (for re-audit)
    const hasResponse = question.response && question.response.answer;

    return (
      <div key={question.id} className="card mb-3">
        <div className="card-body">
          <h6 className="card-title text-primary fw-bold">{question.questionText}</h6>
          
          {/* Show response status for re-audit */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="question-details">
              <p className="mb-2"><strong>Answer Type:</strong> <span className="badge bg-light text-dark">{question.answerType}</span></p>
            </div>
            {hasResponse && (
              <span className="badge bg-success">
                <i className="bi bi-check-circle me-1"></i>
                Answered
              </span>
            )}
          </div>

          {/* Show existing response if available */}
          {hasResponse && (
            <div className="alert alert-info mb-3">
              <h6 className="alert-heading">
                <i className="bi bi-clipboard-check me-2"></i>
                Current Response:
              </h6>
              <p className="mb-1"><strong>Answer:</strong> {question.response.answer}</p>
              {question.response.points && (
                <p className="mb-1"><strong>Points:</strong> <span className="badge bg-primary">{question.response.points}</span></p>
              )}
              {question.response.remarks && (
                <p className="mb-1"><strong>Remarks:</strong> {question.response.remarks}</p>
              )}
              {question.response.cap && (
                <p className="mb-1">
                  <strong>CAP Required:</strong> 
                  <span className={`badge ms-1 ${question.response.cap === 'Yes' ? 'bg-danger' : 'bg-success'}`}>
                    {question.response.cap}
                  </span>
                </p>
              )}
              {question.response.media_file && (
                <p className="mb-0">
                  <strong>Attachment:</strong> 
                  <a href={`http://localhost:8081/uploads/${question.response.media_file}`} 
                     target="_blank" 
                     rel="noopener noreferrer" 
                     className="btn btn-sm btn-outline-primary ms-2">
                    <i className="bi bi-paperclip me-1"></i>
                    View File
                  </a>
                </p>
              )}
            </div>
          )}

          {question.answerType !== 'FreeText' && answerOptions.length > 0 && (
            <div className="answer-options">
              <strong className="text-secondary">Answer Options:</strong>
              <ul className="list-group list-group-flush mt-2">
                {answerOptions.map((option, index) => (
                  <li key={index} className="list-group-item py-1 px-0 border-0">
                    <i className="bi bi-circle-fill text-primary me-2" style={{ fontSize: '0.5rem' }}></i>
                    {option} {optionPoints[index] !== undefined && 
                      <span className="badge bg-secondary ms-2">{optionPoints[index]} points</span>
                    }
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSubsections = (subsections, path = '') => {
    return subsections.map((subsection, index) => {
      const sectionId = `${path}-${index}`;
      const isExpanded = expandedSections[sectionId];
      const hasQuestions = subsection.questions && subsection.questions.length > 0;
      const hasSubsections = subsection.subsections && subsection.subsections.length > 0;

      return (
        <div key={sectionId} className="card mb-2">
          <div 
            className="card-header bg-light cursor-pointer" 
            onClick={() => toggleSection(sectionId)}
            style={{ cursor: 'pointer' }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0 d-flex align-items-center">
                <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} me-2`}></i>
                {subsection.name}
              </h6>
              <div className="d-flex gap-2">
                {hasQuestions && 
                  <span className="badge bg-primary">
                    <i className="bi bi-question-circle me-1"></i>
                    {subsection.questions.length}
                  </span>
                }
                {hasSubsections && 
                  <span className="badge bg-info">
                    <i className="bi bi-folder me-1"></i>
                    {subsection.subsections.length}
                  </span>
                }
              </div>
            </div>
          </div>
          
          {isExpanded && (
            <div className="card-body">
              {hasQuestions && (
                <button 
                  className="btn btn-outline-primary btn-sm mb-3"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSubsectionClick(subsection, path);
                  }}
                >
                  <i className="bi bi-eye me-1"></i>
                  View Questions ({subsection.questions.length})
                </button>
              )}
              
              {hasSubsections && (
                <div className="nested-subsections ms-3">
                  {renderSubsections(subsection.subsections, `${path}|${subsection.name}`)}
                </div>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  const renderChecklistGrid = (checklistArray, status) => {
    if (checklistArray.length === 0) {
      return (
        <div className="text-center py-5">
          <i className="bi bi-clipboard-x display-1 text-muted mb-3"></i>
          <p className="text-muted">No {status.toLowerCase()} business re-audit checklists found for your department.</p>
        </div>
      );
    }

    return (
      <div className="row g-3">
        {checklistArray.map((checklist) => (
          <div key={checklist.id} className="col-lg-4 col-md-6 col-sm-12">
            <div 
              className="card h-100 shadow-sm hover-card border-warning"
              onClick={() => handleChecklistClick(checklist)}
              style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <h6 className="card-title text-primary fw-bold mb-0">
                    <i className="bi bi-arrow-repeat me-2 text-warning"></i>
                    {checklist.checklist_name}
                  </h6>
                  <span className={getStatusBadgeClass(status)}>
                    {status}
                  </span>
                </div>
                <div className="card-text">
                  <div className="mb-2">
                    <i className="bi bi-building me-2 text-muted"></i>
                    <small><strong>Branch:</strong> {checklist.branches}</small>
                  </div>
                 
                  <div className="mb-2">
                    <i className="bi bi-diagram-3 me-2 text-muted"></i>
                    <small><strong>Department:</strong> {department}</small>
                  </div>
                  <div className="mb-0">
                    <i className="bi bi-arrow-repeat me-2 text-warning"></i>
                    <small className="text-warning fw-bold">Business Re-Audit Checklist</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <Container fluid className="px-4 py-5">
        <div className="text-center py-5">
          <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-primary fw-bold mb-1">Loading Business Department Re-Audits</p>
          <p className="text-muted">Please wait while we fetch your business audit information...</p>
        </div>
      </Container>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="text-center mb-4">
            <h1 className="display-5 fw-bold text-warning mb-2">
              <i className="bi bi-arrow-repeat me-3"></i>
              Business Department Re-Audits
            </h1>
            <p className="lead text-muted">
              Department: <span className="fw-bold text-dark">{department}</span>
            </p>
          </div>
        </div>
      </div>

      {!selectedChecklist ? (
        /* Checklists Overview */
        <div className="row">
          <div className="col-12">
            {/* Tabs */}
            <ul className="nav nav-pills nav-fill mb-4" role="tablist">
              <li className="nav-item" role="presentation">
                <button 
                  className={`nav-link ${activeTab === 'pending' ? 'active' : ''}`}
                  onClick={() => setActiveTab('pending')}
                  type="button"
                >
                  <i className="bi bi-hourglass-split me-2"></i>
                  Pending ({checklists.pending.length})
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button 
                  className={`nav-link ${activeTab === 'inProgress' ? 'active' : ''}`}
                  onClick={() => setActiveTab('inProgress')}
                  type="button"
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  In Progress ({checklists.inProgress.length})
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button 
                  className={`nav-link ${activeTab === 'completed' ? 'active' : ''}`}
                  onClick={() => setActiveTab('completed')}
                  type="button"
                >
                  <i className="bi bi-check-circle me-2"></i>
                  Completed ({checklists.completed.length})
                </button>
              </li>
            </ul>

            {/* Tab Content */}
            <div className="tab-content">
              {activeTab === 'pending' && renderChecklistGrid(checklists.pending, 'Pending')}
              {activeTab === 'inProgress' && renderChecklistGrid(checklists.inProgress, 'In Progress')}
              {activeTab === 'completed' && renderChecklistGrid(checklists.completed, 'Completed')}
            </div>
          </div>
        </div>
      ) : (
        /* Checklist Detail View */
        <div className="row">
          <div className="col-12">
            {/* Detail Header */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 p-3 bg-light rounded border-warning border-start border-4">
              <div className="d-flex align-items-center mb-3 mb-md-0">
                <button 
                  className="btn btn-outline-secondary me-3"
                  onClick={() => setSelectedChecklist(null)}
                >
                  <i className="bi bi-arrow-left me-2"></i>Back to Business Re-Audits
                </button>
                <div>
                  <h3 className="mb-1 text-primary">
                    <i className="bi bi-arrow-repeat me-2 text-warning"></i>
                    {selectedChecklist.checklist_name}
                  </h3>
                  <div className="d-flex gap-2">
                    <span className={getStatusBadgeClass(selectedChecklist.status)}>
                      {selectedChecklist.status}
                    </span>
                    <span className="badge bg-warning text-dark">
                      <i className="bi bi-arrow-repeat me-1"></i>
                      Business Re-Audit
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {checklistContent && (
              <div className="row">
                <div className="col-lg-8 col-md-12">
                  {/* Department Section */}
                  <div className="card mb-4 border-warning">
                    <div className="card-header bg-warning text-dark">
                      <h4 className="mb-0">
                        <i className="bi bi-diagram-3 me-2"></i>
                        Department: {checklistContent.name}
                        <span className="badge bg-dark ms-2">Business Re-Audit</span>
                      </h4>
                    </div>
                    <div className="card-body">
                      {/* Direct Questions */}
                      {checklistContent.questions && checklistContent.questions.length > 0 && (
                        <div className="mb-4">
                          <h5 className="text-secondary mb-3">
                            <i className="bi bi-question-circle me-2"></i>
                            Department Questions ({checklistContent.questions.length})
                          </h5>
                          <div className="questions-list">
                            {questions
                              .filter(q => checklistContent.questions.includes(q.id))
                              .map(question => renderQuestionDetails(question))}
                          </div>
                        </div>
                      )}

                      {/* Subsections */}
                      {checklistContent.subsections && checklistContent.subsections.length > 0 && (
                        <div>
                          <h5 className="text-secondary mb-3">
                            <i className="bi bi-folder me-2"></i>
                            Subsections
                          </h5>
                          {renderSubsections(checklistContent.subsections)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Selected Subsection Questions Sidebar */}
                {selectedSubsection && (
                  <div className="col-lg-4 col-md-12">
                    <div className="card border-warning">
                      <div className="card-header bg-warning text-dark d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">
                          <i className="bi bi-eye me-2"></i>
                          {selectedSubsection.name}
                        </h6>
                        <button 
                          className="btn btn-sm btn-outline-dark"
                          onClick={() => {
                            setSelectedSubsection(null);
                            setSubsectionQuestions([]);
                          }}
                        >
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </div>
                      <div className="card-body" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                        <div className="questions-list">
                          {subsectionQuestions.map(question => renderQuestionDetails(question))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BusinessDuserReAudit;